import { Injectable } from '@angular/core';
import { AuthUser } from '../models/user';
import { ApiService, ABCisApi } from '../handlers/apiHandler.service';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { environment as environment } from 'src/environments/environment.dev';
import { LoggerService } from '../handlers/logger.service';
import { HttpStatus } from '../constants/constants';

declare const Buffer;

@Injectable({
  providedIn: 'root'
})

export class AuthService {

  constructor(private ApiService: ApiService,
    private cookieService: CookieService,
    private logger: LoggerService) {}

  /**
   * Checks the users username and password to see if they are able to login.
   * @param userInfo The username and password object
   */
  public userLogin(userInfo: AuthUser): Promise<boolean> {
    // Login the user as password
    const form = new FormData();
    form.append('grant_type', 'password');
    form.append('scope', 'read write');
    form.append('username', userInfo.username);
    form.append('password', userInfo.password);

    this.logger.debug('Logging in user');
    return new Promise((resolve, reject) => {
      this.ApiService.authRequest<any>(ABCisApi.Auth, 'oauth/token', form).then((httpResponse) => {
        const response = httpResponse.body;
        if (response.access_token !== undefined && response.access_token !== null
          && response.refresh_token !== undefined && response.refresh_token !== null) {

          this.logger.debug('Successful Login, Setting tokens');
          // Set their access token inside some cookies
          this.setCookie('USER_TOKEN', response.access_token);
          this.setCookie('REFRESH_TOKEN', response.refresh_token);

          this.logger.debug('Finished setting tokens');
          resolve(true);
        }
        reject(false);
      }).catch((response: HttpErrorResponse) => {
        const errorDescription = response?.error?.error_description ||
          response?.error?.message ||
          response?.message ||
          `Authentication failed (Status: ${response.status})`;
        reject(errorDescription);
      });
    });
  }

  /**
   * Sets a cookie based on the environments vairables
   * @param cookieName Cookie Name
   * @param access_token Cookie Value
   */
  setCookie(cookieName: string, access_token: any) {
    this.logger.debug(`Setting cookie ${cookieName} to ${environment.domain}`);
    this.cookieService.set(cookieName, <string>access_token, undefined, '/', environment.domain, environment.isSecure, 'Strict' );
  }

  /**
   * We check if the user is logged in by checking their access token.
   * Then hit an endpoint that checks their authority
   */
  public async isLoggedIn(): Promise<boolean> {
    if (this.cookieService.check('USER_TOKEN') && this.cookieService.check('REFRESH_TOKEN')) {
      return await this.checkAuthority();
    } else {
      return false;
    }
  }

  /**
   * Reset a user password if reset pass token is valid
   * @param resetPasswordToken The reset password token
   * @param newPassword The new password
   */
  public async userRequestPasswordReset(
    email: string
  ): Promise<boolean> {
    const form = new FormData();
    form.append('grant_type', 'client_credentials');
    form.append('scope', 'read write');

    return new Promise((resolve, reject) => {
      this.ApiService.authRequest<any>(ABCisApi.Auth, `user/reset-password/request?email=${email}`, form).then((httpResponse) => {
        resolve(true);
      }).catch((errResponse: HttpErrorResponse) => {
        reject(errResponse);
      });
    });
  }

  /**
   * Checks to see if the user is authorised to see this website
   */
  public async checkAuthority(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.ApiService.GetUrlConfigurations().then(() => {
        this.ApiService.getABCisRequest(ABCisApi.Support, 'monitor/authority-check', {responseType: 'text'})
          .then((response:HttpResponse<any>) => {
            resolve(true);
          }).catch((errorResponse: HttpErrorResponse) => {
            if (errorResponse.status == HttpStatus.FORBIDDEN) {
              resolve (false);
            }
            resolve (false);
          });
      }).catch((error: HttpErrorResponse) => {
        resolve (false);
      });
    });
  }

  /**
   * Log out the user.
   */
  public logout(){
    this.removeToken();
  }

  /**
   * Delete access token.
   */
  public removeToken(){
    this.cookieService.delete('USER_TOKEN', '/', environment.domain);
    this.cookieService.delete('REFRESH_TOKEN', '/', environment.domain);
  }

}
